/**
 * Adaptive navigation camera.
 *
 * The requirement driving this module: the driver must SEE which lane to take.
 * Arrow glyphs help, but the thing that actually communicates lanes is the map
 * itself zoomed far enough in that individual lane markings and slip roads are
 * distinguishable, tilted so the road ahead occupies the screen.
 *
 * Strategy:
 *  - Cruise state: zoom scales inversely with speed (fast = zoomed out to see
 *    further ahead), high pitch, camera behind the vehicle.
 *  - Approach state: as distance-to-maneuver falls below a speed-dependent
 *    threshold, zoom ramps smoothly up to junction level (~17.5+) and pitch
 *    drops, giving a clearer top-down read of the lane layout.
 *  - Complex junctions (forks, ramps, roundabouts, maneuvers with lane data)
 *    zoom harder than plain turns.
 *  - After the maneuver the camera relaxes back to cruise.
 *
 * All values are continuous functions of (speed, distance) rather than modes
 * with thresholds, so the camera never visibly "switches" - it breathes.
 */

import type { NavProgress } from '../core/types';

/**
 * The driver's camera choices, from Settings.
 *
 * `pitch` is the tilt used both cruising and browsing - 0/15/30/45/60, a
 * snapped slider. `driveZoom` fixes the cruise zoom at one level the driver
 * chose; 0 means auto, the speed-scaled zoom below. Neither disables the
 * junction approach: zooming into an intersection is guidance, not taste.
 */
export interface CameraPrefs {
  pitch: number;
  driveZoom: number;
}

const DEFAULT_PREFS: CameraPrefs = { pitch: 58, driveZoom: 0 };

export interface CameraTarget {
  zoom: number;
  pitch: number;
  /** degrees, map bearing = direction of travel */
  bearing: number;
  /** push the vehicle towards the lower third of the screen */
  padding: { top: number; bottom: number; left: number; right: number };
  /** ms the easing should take; roughly the fix interval so motion is continuous */
  duration: number;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
/** Smoothstep - continuous first derivative, no visible kink. */
const smooth = (t: number): number => {
  const x = clamp(t, 0, 1);
  return x * x * (3 - 2 * x);
};

/** Maneuvers where seeing the road layout matters most. */
const COMPLEX_TYPES = new Set(['fork', 'off ramp', 'on ramp', 'roundabout', 'rotary', 'merge']);

export function cruiseZoom(speedMs: number): number {
  // 0 km/h -> 17, 30 km/h -> ~16.3, 60 km/h -> ~15.6, 100+ km/h -> 14.8
  const kmh = speedMs * 3.6;
  return clamp(17 - kmh * 0.022, 14.8, 17);
}

/**
 * Where the vehicle should sit on screen.
 *
 * Portrait: pushed down towards the lower third, so most of the screen is the
 * road ahead. Landscape: the instruction card takes the left 44% of a short
 * wide screen, so centring the vehicle puts it directly behind the card. Left
 * padding shifts the map centre - and with it the vehicle - into the open area
 * on the right, which is what the driver actually sees.
 */
function framing(
  viewportWidth: number,
  viewportHeight: number,
  topFactor: number,
): { top: number; bottom: number; left: number; right: number } {
  const landscape = viewportWidth > viewportHeight && viewportHeight < 620;
  return {
    top: viewportHeight * (landscape ? topFactor * 0.72 : topFactor),
    bottom: 0,
    left: landscape ? Math.min(viewportWidth * 0.46, 430) : 0,
    right: 0,
  };
}

export function computeCamera(
  progress: NavProgress | null,
  heading: number,
  speedMs: number,
  viewportHeight: number,
  viewportWidth = viewportHeight,
  prefs: CameraPrefs = DEFAULT_PREFS,
): CameraTarget {
  const base: CameraTarget = {
    zoom: prefs.driveZoom > 0 ? prefs.driveZoom : cruiseZoom(speedMs),
    pitch: prefs.pitch,
    bearing: heading,
    padding: framing(viewportWidth, viewportHeight, 0.42),
    duration: 950,
  };
  if (!progress) return base;

  const d = progress.distanceToManeuver;
  // The maneuver we're driving TOWARDS, not the one we already passed at the
  // start of the current step - that older reading kept the camera flat
  // through the junction it was supposed to open up for.
  const type = progress.maneuverStep?.maneuver.type ?? progress.step.maneuver.type;
  const isArrival = type === 'arrive';
  const complex =
    COMPLEX_TYPES.has(type) ||
    (progress.lanes?.length ?? 0) > 0 ||
    progress.laneAdvice?.changeRequired === true;

  // Start the zoom-in earlier when driving faster: at 90 km/h you need the
  // junction view ~500 m out; at 30 km/h, 200 m is plenty.
  const approachStart = clamp(180 + speedMs * 3.6 * 3.5, 220, 550);
  // Fully zoomed by this distance.
  const approachEnd = 60;

  if (d >= approachStart || isArrival) return base;

  const t = smooth(1 - (d - approachEnd) / (approachStart - approachEnd)); // 0..1

  // Junction zoom level: complex junctions get closer. Never BELOW the cruise
  // zoom - a driver who fixed the zoom at 17.5 must not watch the camera pull
  // OUT on the approach because a plain turn's 17.2 happens to be less.
  const junctionZoom = Math.max(complex ? 17.8 : 17.2, base.zoom);
  // Lower the pitch through the approach - a flatter view separates parallel
  // lanes and slip roads far better than a raked one. Only ever lower: a
  // driver who chose a flat map stays flat through the junction too.
  const junctionPitch = Math.min(complex ? 32 : 40, base.pitch);

  return {
    zoom: lerp(base.zoom, junctionZoom, t),
    pitch: lerp(base.pitch, junctionPitch, t),
    bearing: heading,
    padding: framing(viewportWidth, viewportHeight, lerp(0.42, 0.3, t)),
    duration: 950,
  };
}

/**
 * Overview camera fitting the whole route, for the preview screen.
 *
 * `sheetHeight` is the MEASURED height of the preview sheet. It used to be a
 * hardcoded 300 px, but the sheet grows with the number of options, the advice
 * card and the origin row - on a phone it can take two thirds of the screen.
 * The route was then fitted to a viewport that did not exist, so it was drawn
 * behind the sheet: "when picking route, we cant see the route."
 */
export function previewPadding(
  isPortrait: boolean,
  sheetHeight = 300,
  viewportHeight = 800,
  sheetWidth = 0,
  viewportWidth = 400,
): { top: number; bottom: number; left: number; right: number } {
  // Wide screens put the sheet down the left as a panel, so the space it takes
  // is horizontal. Padding down instead would centre the route underneath it.
  if (viewportWidth >= 900) {
    const left = Math.min(sheetWidth + 40, viewportWidth * 0.55);
    return { top: 70, bottom: 70, left: Math.max(80, left), right: 70 };
  }
  // Never let the padding swallow the viewport - fitBounds fails outright when
  // top + bottom exceeds the canvas height.
  const bottom = Math.min(sheetHeight + 24, Math.max(120, viewportHeight * 0.62));
  return isPortrait
    ? { top: 80, bottom, left: 40, right: 40 }
    : { top: 70, bottom: 70, left: 380, right: 60 };
}
